import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Dialog } from '../../../core/providers/modal/modal-types';

@Component({
    selector: 'vsf-add-to-cart-dialog',
    template: `
        <div class="relative flex max-h-[90vh] min-h-0 w-full max-w-[calc(100vw-2rem)] flex-col gap-4 overflow-y-auto overscroll-y-contain rounded-lg bg-white p-4 pt-14 sm:p-6 sm:pt-16 md:max-h-[90vh] md:max-w-[min(1200px,calc(100vw-2rem))] md:h-[600px] md:flex-row md:gap-10 md:overflow-hidden md:p-10 md:pt-10">
            <!-- Close button -->
            <button class="absolute top-4 right-4 z-10 text-heading hover:text-primary transition-colors md:top-6 md:right-6"
                    type="button"
                    (click)="close()">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
            </button>

            <!-- Left: Product Images -->
            <div class="flex w-full shrink-0 flex-col gap-2 md:h-full md:min-h-0 md:w-auto">
                <div class="flex w-full shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface md:min-h-0 md:flex-1 md:aspect-[722/956]">
                    <img *ngIf="getActiveImage()"
                         [src]="getActiveImage() + '?preset=medium'"
                         [alt]="product?.name"
                         class="h-auto w-full object-contain md:max-h-full md:max-w-full" />
                </div>
                <div class="flex gap-2 overflow-x-auto" *ngIf="getAllImages().length > 1">
                    <button *ngFor="let img of getAllImages(); let i = index"
                            (click)="activeImageIndex = i"
                            class="shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-colors"
                            [class.border-primary]="activeImageIndex === i"
                            [class.border-transparent]="activeImageIndex !== i">
                        <img [src]="img.preview + '?preset=tiny'"
                             [alt]="'Image ' + (i + 1)"
                             class="w-full h-full object-cover" />
                    </button>
                </div>
            </div>

            <!-- Right: Product Info — outer scroll on small screens; from md, scroll title+about in one region so laptop always scrolls -->
            <div class="flex w-full min-w-0 flex-col md:h-full md:min-h-0 md:flex-1">
                <div class="flex min-h-0 flex-col gap-6 md:min-h-0 md:flex-1 md:gap-10 md:overflow-y-auto md:overscroll-y-contain">
                    <!-- Name & Price -->
                    <div class="flex shrink-0 flex-col gap-3 md:gap-4">
                        <div class="flex flex-col gap-1">
                            <p class="text-disabled text-base md:text-lg">{{ variant?.sku }}</p>
                            <h2 class="text-heading text-xl font-semibold leading-tight sm:text-2xl md:text-[32px] md:leading-[40px]">
                                {{ product?.name }}
                            </h2>
                        </div>
                        <div class="flex flex-wrap items-center gap-2 md:gap-3">
                            <span *ngIf="hasDiscount()" class="text-disabled text-lg leading-snug line-through sm:text-xl md:text-[24px] md:leading-[40px]">
                                {{ variant?.priceWithTax | formatPrice }}
                            </span>
                            <span class="text-primary text-xl font-semibold leading-snug sm:text-2xl md:text-[32px] md:leading-[40px]">
                                {{ getDisplayPrice() | formatPrice }}
                            </span>
                        </div>
                        <div *ngIf="showInstallationRow()" class="flex items-center gap-2 text-sm">
                            <span class="text-body">+ {{ 'product.installationService' | translate }}:</span>
                            <span class="font-semibold text-primary">{{ installationPrice | formatPrice }}</span>
                        </div>
                    </div>

                    <!-- About -->
                    <div class="flex flex-col gap-2 border-t border-disabled pt-6 md:pt-10">
                        <h3 class="text-heading text-lg font-semibold">{{ 'product.aboutThisProduct' | translate }}</h3>
                        <div class="text-body text-sm leading-4"
                             [innerHTML]="product?.description | safeHtml"></div>
                    </div>
                </div>

                <!-- Bottom: Quantity + Add to Cart -->
                <div class="flex shrink-0 items-center gap-2 pt-4 pb-2 md:pb-0">
                    <div class="relative h-[52px] w-[80px] shrink-0 rounded-lg bg-surface sm:h-[60px] sm:w-[90px]">
                        <select [(ngModel)]="qty"
                                class="qty-select absolute inset-0 w-full h-full bg-transparent border-none text-heading text-base font-semibold text-center focus:outline-none cursor-pointer"
                                style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none; padding-inline-start: 1rem; padding-inline-end: 2rem;">
                            <option *ngFor="let n of qtyOptions" [ngValue]="n">{{ n }}</option>
                        </select>
                        <svg class="w-5 h-5 text-heading absolute top-1/2 -translate-y-1/2 pointer-events-none" style="inset-inline-end: 0.75rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                    </div>
                    <button type="button"
                            (click)="addToCart()"
                            class="h-[52px] flex-1 rounded-lg bg-primary text-sm font-semibold uppercase text-white transition-colors hover:bg-primary-600 sm:h-[60px] sm:text-base">
                        {{ 'product.addToCart' | translate }}
                    </button>
                </div>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToCartDialogComponent implements Dialog<{ qty: number } | undefined> {
    resolveWith: (result?: { qty: number }) => void;

    variant: any;
    product: any;
    installationSelected = false;
    installationPrice = 0;
    variantDiscounts: { [variantId: string]: number } = {};
    qty = 1;
    qtyOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    activeImageIndex = 0;

    showInstallationRow(): boolean {
        return this.installationSelected && this.installationPrice > 0;
    }

    hasDiscount(): boolean {
        if (!this.variant) return false;
        const discount = this.variantDiscounts[this.variant.id];
        return discount != null && discount > 0;
    }

    getDisplayPrice(): number {
        if (!this.variant) return 0;
        const price = this.variant.priceWithTax as number;
        const discount = this.variantDiscounts[this.variant.id];
        if (discount && discount > 0) {
            return Math.round(price * (1 - discount / 100));
        }
        return price;
    }

    getAllImages(): Array<{ preview: string }> {
        const variantAssets = this.variant?.assets;
        if (variantAssets?.length) {
            return variantAssets;
        }
        const productAssets = this.product?.assets;
        if (productAssets?.length) {
            return productAssets;
        }
        const fallback = this.variant?.featuredAsset || this.product?.featuredAsset;
        return fallback ? [fallback] : [];
    }

    getActiveImage(): string | null {
        const images = this.getAllImages();
        return images[this.activeImageIndex]?.preview || null;
    }

    addToCart() {
        this.resolveWith({ qty: this.qty });
    }

    close() {
        this.resolveWith(undefined);
    }
}
